import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LogoLockup } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COMPANY = {
  name: "Cleveland Creative Group",
  website: "https://clevelandcreative.group",
  supportPhone: "833-716-5522",
  supportEmail: "jamursch@gmail.com",
  privacyUrl: "/privacy",
  termsUrl: "/terms",
};

const OPT_IN_TEXT = `I agree to receive text messages from ${COMPANY.name} at the mobile number provided regarding my project, account, or inquiry, including project updates, appointment reminders, account-servicing communications, and customer support responses. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out and HELP for help. Consent is not required as a condition of any purchase, product, or service.`;

/* ---------- Layout primitives ---------- */
const Section = ({ id, eyebrow, title, children }) => (
  <section id={id} className="scroll-mt-24 mb-12">
    {eyebrow && (
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-3">
        {eyebrow}
      </div>
    )}
    <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-50 mb-5">
      {title}
    </h2>
    <div className="text-slate-300 leading-relaxed space-y-4 text-[15px] md:text-base">
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3">
    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
    <span>{children}</span>
  </li>
);

/* ---------- Embedded opt-in form ---------- */
const ConsentForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    consent_given: false,
    terms_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedRecord, setSubmittedRecord] = useState(null);

  const canSubmit = useMemo(
    () =>
      form.name.trim().length > 0 &&
      form.phone.trim().length >= 10 &&
      form.consent_given &&
      form.terms_accepted &&
      !submitting,
    [form, submitting]
  );

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please complete all required fields and check both boxes.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      const res = await axios.post(`${API}/sms-consent`, payload);
      setSubmittedRecord(res.data);
      toast.success("Consent recorded. You'll receive a confirmation text shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail[0]?.msg
        : typeof detail === "string"
        ? detail
        : "Could not record consent. Please try again or email " + COMPANY.supportEmail + ".";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedRecord) {
    return (
      <div
        data-testid="consent-success"
        className="rounded-2xl border border-cyan-400/40 bg-gradient-to-b from-slate-900 to-slate-950 p-8 md:p-10 shadow-cyan-glow"
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="h-10 w-10 rounded-full bg-cyan-400/15 border border-cyan-400/40 grid place-items-center">
            <Check className="h-5 w-5 text-cyan-300" />
          </span>
          <h3 className="font-display text-2xl font-bold tracking-tight text-slate-50">
            Consent recorded
          </h3>
        </div>
        <p className="text-slate-300 leading-relaxed mb-6">
          Thank you, {submittedRecord.name.split(" ")[0]}. We've logged your
          consent to receive text messages at{" "}
          <span className="font-mono text-cyan-300">{submittedRecord.phone}</span>{" "}
          on{" "}
          <span className="font-mono text-slate-200">
            {new Date(submittedRecord.created_at).toLocaleString()}
          </span>
          . Reply <strong>STOP</strong> to any message to opt out, or{" "}
          <strong>HELP</strong> for assistance at any time.
        </p>
        <div className="rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 font-mono text-xs text-slate-400">
          Record ID · {submittedRecord.id}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="sms-consent-form"
      className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6 md:p-10 shadow-cyan-glow"
    >
      <div className="flex items-start gap-3 mb-6">
        <ShieldCheck className="h-5 w-5 text-cyan-300 mt-0.5" />
        <div>
          <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-slate-50">
            Provide Your Consent
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Required fields are marked with an asterisk (*). Both checkboxes
            must be selected to submit.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label
            htmlFor="sms-name"
            className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block"
          >
            Full name *
          </Label>
          <Input
            id="sms-name"
            value={form.name}
            onChange={update("name")}
            data-testid="sms-name"
            placeholder="Jordan Mitchell"
            className="bg-slate-950 border-slate-800 h-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400"
          />
        </div>
        <div>
          <Label
            htmlFor="sms-phone"
            className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block"
          >
            Mobile phone *
          </Label>
          <Input
            id="sms-phone"
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            data-testid="sms-phone"
            placeholder="(216) 555-0123"
            className="bg-slate-950 border-slate-800 h-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400"
          />
        </div>
        <div className="md:col-span-2">
          <Label
            htmlFor="sms-email"
            className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block"
          >
            Email (optional)
          </Label>
          <Input
            id="sms-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            data-testid="sms-email"
            placeholder="jordan@example.com"
            className="bg-slate-950 border-slate-800 h-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400"
          />
        </div>
      </div>

      {/* Required consent disclosures */}
      <div className="mt-8 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer group">
          <Checkbox
            checked={form.consent_given}
            onCheckedChange={(v) =>
              setForm((f) => ({ ...f, consent_given: !!v }))
            }
            data-testid="sms-consent-checkbox"
            className="mt-0.5 border-slate-700 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400 data-[state=checked]:text-slate-950"
          />
          <span className="text-sm text-slate-300 leading-relaxed">
            {OPT_IN_TEXT}
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer group">
          <Checkbox
            checked={form.terms_accepted}
            onCheckedChange={(v) =>
              setForm((f) => ({ ...f, terms_accepted: !!v }))
            }
            data-testid="sms-terms-checkbox"
            className="mt-0.5 border-slate-700 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400 data-[state=checked]:text-slate-950"
          />
          <span className="text-sm text-slate-300 leading-relaxed">
            I have read and agree to the{" "}
            <a
              href={COMPANY.privacyUrl}
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href={COMPANY.termsUrl}
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
            >
              Terms of Service
            </a>
            .
          </span>
        </label>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs text-slate-500 max-w-md">
          By submitting this form, your consent is recorded with a timestamp and
          IP address for audit purposes. Carriers may review this record.
        </p>
        <Button
          type="submit"
          disabled={!canSubmit}
          data-testid="sms-consent-submit"
          className="h-12 px-8 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_0_1px_rgba(0,229,255,0.35),0_14px_40px_-12px_rgba(0,229,255,0.55)]"
        >
          {submitting ? "Recording…" : "Submit consent"}
        </Button>
      </div>
    </form>
  );
};

/* ---------- Page ---------- */
const SmsTerms = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/60">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <span className="hidden sm:block">
              <LogoLockup size={36} />
            </span>
            <span className="sm:hidden">
              <LogoLockup size={28} />
            </span>
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-cyan-300 transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
        {/* Hero */}
        <div className="mb-14 md:mb-20 max-w-3xl">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
            Carrier-compliant disclosure
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-[0.98] mb-6 text-balance">
            SMS Terms <span className="text-gradient-cyan">& Consent</span>
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            This page explains how the {COMPANY.name} text messaging program
            works, the types of messages you may receive, how to get help, and
            how to stop messages at any time. This page also captures your
            consent if you choose to opt in.
          </p>
          <p className="text-slate-500 text-sm mt-4">
            Last updated:{" "}
            {new Date().toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>

        {/* Table of contents */}
        <nav
          aria-label="On this page"
          className="mb-14 rounded-2xl border border-slate-800 bg-slate-900/40 p-5 md:p-6"
        >
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-3">
            On this page
          </div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[
              ["who-we-are", "Who We Are"],
              ["how-you-opt-in", "How You Opt In"],
              ["message-types", "Types of Messages"],
              ["frequency", "Message Frequency"],
              ["rates", "Message and Data Rates"],
              ["help-stop", "Help or Stop Messages"],
              ["revocation", "Revocation of Consent"],
              ["privacy-terms", "Privacy and Terms"],
              ["consent-form", "Provide Your Consent"],
              ["contact", "Contact Us"],
            ].map(([href, label], i) => (
              <li key={href} className="flex items-baseline gap-3">
                <span className="font-mono text-xs text-slate-600">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <a
                  href={`#${href}`}
                  className="text-slate-300 hover:text-cyan-300 transition-colors"
                >
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Content sections */}
        <Section id="who-we-are" eyebrow="01" title="Who We Are">
          <p>
            <strong className="text-slate-100">{COMPANY.name}</strong> ("we,"
            "us," or "our") is the sender of any text messages you may receive
            through this program. We may engage authorized service providers to
            transmit messages on our behalf, but those communications remain
            sent in our name and subject to the terms below.
          </p>
        </Section>

        <Section id="how-you-opt-in" eyebrow="02" title="How You Opt In">
          <p>
            We will only send you text messages after you have provided
            affirmative consent. You may opt in to the program through any of
            the following methods:
          </p>
          <ul className="space-y-2 mt-2">
            <Bullet>Submitting a form on our website, including this page</Bullet>
            <Bullet>Submitting a contact or project inquiry form that requests text follow-up</Bullet>
            <Bullet>
              Providing verbal authorization to one of our representatives,
              where permitted by applicable law
            </Bullet>
            <Bullet>
              Texting a designated keyword to our toll-free number, where
              offered
            </Bullet>
          </ul>
          <p>
            Your consent must be voluntary and affirmative. We will not enroll
            you in the program without your express permission, and we will not
            require text consent as a condition of any purchase, product, or
            service.
          </p>
        </Section>

        <Section
          id="message-types"
          eyebrow="03"
          title="Types of Messages You May Receive"
        >
          <p>Depending on your relationship with us, you may receive:</p>
          <ul className="space-y-2 mt-2">
            <Bullet>Project status updates</Bullet>
            <Bullet>Appointment, meeting, or call reminders</Bullet>
            <Bullet>Account and inquiry follow-ups</Bullet>
            <Bullet>Servicing and account-management communications</Bullet>
            <Bullet>Customer support responses</Bullet>
            <Bullet>Verification codes and security notifications, where applicable</Bullet>
          </ul>
        </Section>

        <Section id="frequency" eyebrow="04" title="Message Frequency">
          <p>
            Message frequency varies based on your project, account activity,
            and your interaction with our team. You will not receive
            promotional or unrelated marketing messages through this program
            unless you have separately opted in to receive them.
          </p>
        </Section>

        <Section id="rates" eyebrow="05" title="Message and Data Rates">
          <p>
            Message and data rates may apply according to your wireless
            carrier's plan. {COMPANY.name} is not responsible for any charges
            your carrier may assess for sending or receiving text messages.
            Please contact your carrier for details about your specific plan.
          </p>
        </Section>

        <Section
          id="help-stop"
          eyebrow="06"
          title="How to Get Help or Stop Messages"
        >
          <p>
            <strong className="text-slate-100">For help:</strong> Reply{" "}
            <span className="font-mono text-cyan-300">HELP</span> to any text
            message you receive from us, or contact us directly using the
            information in the <a href="#contact" className="text-cyan-300 underline underline-offset-2">Contact Us</a>{" "}
            section below.
          </p>
          <p>
            <strong className="text-slate-100">To stop messages:</strong> Reply{" "}
            <span className="font-mono text-cyan-300">STOP</span> to any text
            message you receive from us. You may opt out of text messaging at
            any time by replying STOP to any message. Upon receipt of your
            request, we will cease sending text messages to that mobile number
            except as required to confirm your opt-out request.
          </p>
          <p>
            You may also revoke consent by phone at{" "}
            <a
              href={`tel:+1${COMPANY.supportPhone.replace(/\D/g, "")}`}
              className="font-mono text-slate-100 hover:text-cyan-300"
            >
              {COMPANY.supportPhone}
            </a>{" "}
            or by email at{" "}
            <a
              href={`mailto:${COMPANY.supportEmail}`}
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
            >
              {COMPANY.supportEmail}
            </a>
            . Opt-out requests are honored promptly.
          </p>
        </Section>

        <Section id="revocation" eyebrow="07" title="Revocation of Consent">
          <p>You may revoke your consent at any time and at no cost. Please note:</p>
          <ul className="space-y-2 mt-2">
            <Bullet>
              Revocation applies to the specific mobile number from which you
              opted in
            </Bullet>
            <Bullet>
              Revocation does not affect communications made prior to receipt of
              your opt-out request
            </Bullet>
            <Bullet>
              We may send a final confirmation message acknowledging your
              opt-out request before all messages stop
            </Bullet>
          </ul>
        </Section>

        <Section id="privacy-terms" eyebrow="08" title="Privacy and Terms">
          <p>
            For additional information about how we handle your personal
            information, please review our{" "}
            <a
              href={COMPANY.privacyUrl}
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
            >
              Privacy Policy
            </a>{" "}
            and our{" "}
            <a
              href={COMPANY.termsUrl}
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2"
            >
              Terms of Service
            </a>
            .
          </p>
          <p>
            Your consent to receive text messages under this program is{" "}
            <strong className="text-slate-100">separate</strong> from any other
            agreement you have with {COMPANY.name}, including any service or
            account agreement. Consent to receive text messages is not required
            as a condition of any purchase, product, or service. You may
            decline text messages and continue using all other available
            communication channels.
          </p>
        </Section>

        <Section
          id="consent-form"
          eyebrow="09"
          title="Provide Your Consent"
        >
          <p>
            Complete the form below to enroll in the {COMPANY.name} text
            messaging program. Your consent will be recorded with a timestamp,
            the IP address used to submit the form, and the exact opt-in
            language shown to you at the time of submission. This record is
            maintained for audit and carrier compliance purposes.
          </p>
          <div className="pt-2">
            <ConsentForm />
          </div>
        </Section>

        <Section id="contact" eyebrow="10" title="Contact Us">
          <p>
            For questions about our text messaging program, to revoke consent
            outside of replying STOP, or to request more information, please
            contact us:
          </p>
          <div className="mt-3 rounded-xl border border-slate-800 bg-slate-900/60 p-5 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mb-1">
                  Company
                </div>
                <div className="text-slate-100 font-semibold">
                  {COMPANY.name}
                </div>
                <div className="text-slate-400">Cleveland, Ohio</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mb-1">
                  Toll-Free
                </div>
                <a
                  href={`tel:+1${COMPANY.supportPhone.replace(/\D/g, "")}`}
                  className="text-slate-100 font-mono hover:text-cyan-300"
                >
                  {COMPANY.supportPhone}
                </a>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mb-1">
                  Email
                </div>
                <a
                  href={`mailto:${COMPANY.supportEmail}`}
                  className="text-cyan-300 hover:text-cyan-200"
                >
                  {COMPANY.supportEmail}
                </a>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mb-1">
                  Website
                </div>
                <a
                  href="/"
                  className="text-cyan-300 hover:text-cyan-200"
                >
                  {COMPANY.website}
                </a>
              </div>
            </div>
          </div>
        </Section>
      </main>

      <footer className="border-t border-slate-900 py-10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-slate-500 font-mono">
            © {new Date().getFullYear()} {COMPANY.name} · SMS Terms & Consent
          </div>
          <div className="flex gap-5 text-xs">
            <a href="/privacy" className="text-slate-400 hover:text-cyan-300">
              Privacy Policy
            </a>
            <a href="/terms" className="text-slate-400 hover:text-cyan-300">
              Terms of Service
            </a>
            <a href="/" className="text-slate-400 hover:text-cyan-300">
              Back to site
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SmsTerms;
